import type { PublicParticipant, RoomState, RoomType } from '../../../shared/types.js';
import type { AppSocket } from '../socket';
import { getStoredName, setStoredName, getPreset, setPreset } from '../state';
import { el, nameBadge, showToast } from '../dom';
import { navigate } from '../router';
import { adminFetch, getAdminToken } from '../admin';
import { t } from '../i18n';
import { buildLocalePicker } from '../i18n/picker';

type FullState = RoomState & { youId: string };

export interface RoomPageDeps {
  socket: AppSocket;
}

export function buildRoomPage(s: FullState, deps: RoomPageDeps): HTMLElement {
  const me = s.participants.find((p) => p.id === s.youId);
  const layout = el('div', 'layout');
  layout.appendChild(buildHeader(s, me, deps));
  const main = el('div', 'main-grid');
  main.appendChild(buildParticipantsPane(s, me, deps));
  main.appendChild(buildSidebar(s, me, deps));
  layout.appendChild(main);
  if (s.phase === 'countdown') layout.appendChild(buildCountdownOverlay(s));
  if (s.phase === 'spinning' || s.phase === 'result') layout.appendChild(buildWheelOverlay(s, me, deps));
  return layout;
}

function typeIcon(type: RoomType): string {
  return type === 'games' ? '🎮' : type === 'movies' ? '🎬' : '🌸';
}

function typeLabel(type: RoomType): string {
  return t(`type.${type}` as 'type.games');
}

function buildHeader(s: FullState, me: PublicParticipant | undefined, deps: RoomPageDeps): HTMLElement {
  const header = el('header', 'app-header');
  const left = el('div', 'header-left');
  const back = el('button', 'btn ghost small back-btn', t('room.back')) as HTMLButtonElement;
  back.title = t('room.backTitle');
  back.addEventListener('click', () => {
    deps.socket.emit('leaveRoom');
    navigate('/');
  });
  left.appendChild(back);

  const title = el('div', 'app-title room-title');
  title.appendChild(el('span', 'room-title-text', s.meta.title));
  title.appendChild(el('span', `room-type-badge room-type-${s.meta.type}`, typeIcon(s.meta.type) + ' ' + typeLabel(s.meta.type)));
  title.appendChild(el('span', 'room-code', s.meta.id));
  left.appendChild(title);
  header.appendChild(left);

  const right = el('div', 'header-right');
  right.appendChild(buildLocalePicker());
  if (getAdminToken()) {
    const adminReset = el('button', 'btn ghost small admin-shortcut', t('room.admin.reset')) as HTMLButtonElement;
    adminReset.title = t('room.admin.resetTitle');
    adminReset.disabled = s.phase !== 'lobby' && s.phase !== 'result';
    adminReset.addEventListener('click', async () => {
      if (!confirm(t('room.admin.resetConfirm', { code: s.meta.id }))) return;
      const r = await adminFetch(`/api/admin/rooms/${s.meta.id}/reset`, { method: 'POST' });
      if (r.ok) showToast(t('room.admin.resetDone'), 'info');
      else showToast(t('room.admin.resetFail', { status: r.status }), 'error');
    });
    right.appendChild(adminReset);
  }
  const wrap = el('div', 'name-input-wrap');
  wrap.appendChild(el('label', 'name-label', t('room.nameLabel')));
  const input = document.createElement('input');
  input.className = 'name-input';
  input.value = me?.name ?? getStoredName() ?? '';
  input.maxLength = 32;
  input.placeholder = t('room.namePlaceholder');
  let timer: number | null = null;
  input.addEventListener('input', () => {
    if (timer) clearTimeout(timer);
    timer = window.setTimeout(() => {
      const v = input.value.trim();
      setStoredName(v);
      deps.socket.emit('setName', v);
    }, 250);
  });
  wrap.appendChild(input);
  right.appendChild(wrap);
  header.appendChild(right);
  return header;
}

function buildParticipantsPane(s: FullState, me: PublicParticipant | undefined, deps: RoomPageDeps): HTMLElement {
  const pane = el('section', 'participants');
  if (!me) {
    pane.appendChild(el('div', 'placeholder', t('room.joining')));
    return pane;
  }
  const others = s.participants.filter((p) => p.id !== s.youId);
  const ordered = [me, ...others];
  for (const p of ordered) pane.appendChild(buildParticipantCard(p, s, p.id === s.youId, deps));
  return pane;
}

function buildParticipantCard(
  p: PublicParticipant,
  s: FullState,
  isMe: boolean,
  deps: RoomPageDeps,
): HTMLElement {
  const card = el('article', `participant ${isMe ? 'me' : ''} ${p.ready ? 'ready' : ''} ${p.connected ? '' : 'disconnected'}`);
  card.dataset.key = `participant-${p.id}`;
  card.style.setProperty('--p-color', p.color);
  const head = el('header', 'participant-head');
  head.appendChild(el('div', 'p-dot'));
  const nm = el('div', 'p-name');
  const badge = nameBadge(p.name);
  if (badge) {
    const b = el('span', 'name-badge', badge.emoji);
    b.title = badge.title;
    nm.appendChild(b);
  }
  nm.appendChild(document.createTextNode(p.name + (isMe ? ' ' + t('room.you') : '')));
  head.appendChild(nm);
  head.appendChild(el('div', 'p-status',
    p.ready ? t('room.statusReady')
    : p.connected ? t('room.statusPicking')
    : t('room.statusDisconnected')));
  card.appendChild(head);

  if (isMe) card.appendChild(buildMyPickUI(p, s, deps));
  else card.appendChild(buildTheirPickList(p, s));

  if (isMe) {
    const actions = el('div', 'p-actions');
    const allBtn = el('button', 'btn ghost small', t('room.selectAll')) as HTMLButtonElement;
    allBtn.disabled = s.phase !== 'lobby';
    allBtn.addEventListener('click', () => deps.socket.emit('setAllPicks', true));
    actions.appendChild(allBtn);
    const noneBtn = el('button', 'btn ghost small', t('room.deselectAll')) as HTMLButtonElement;
    noneBtn.disabled = s.phase !== 'lobby' || p.picks.length === 0;
    noneBtn.addEventListener('click', () => deps.socket.emit('setAllPicks', false));
    actions.appendChild(noneBtn);
    const presetBtn = el('button', 'btn ghost small', t('room.preset')) as HTMLButtonElement;
    presetBtn.title = t('room.presetTitle');
    presetBtn.disabled = s.phase !== 'lobby' || getPreset().length === 0;
    presetBtn.addEventListener('click', () => applyPreset(s, p, deps));
    actions.appendChild(presetBtn);
    const saveBtn = el('button', 'btn ghost small', t('room.save')) as HTMLButtonElement;
    saveBtn.title = t('room.saveTitle');
    saveBtn.disabled = s.phase !== 'lobby' || p.picks.length === 0;
    saveBtn.addEventListener('click', () => {
      setPreset(p.picks.map((pk) => pk.gameId));
      showToast(t('room.presetSaved', { n: p.picks.length }), 'info');
    });
    actions.appendChild(saveBtn);
    const ready = el('button', `btn ready-btn ${p.ready ? 'on' : ''}`, p.ready ? t('room.cancelReady') : t('room.ready')) as HTMLButtonElement;
    ready.disabled = (!p.ready && p.picks.length === 0) || (s.phase !== 'lobby' && s.phase !== 'countdown');
    ready.addEventListener('click', () => deps.socket.emit('setReady', !p.ready));
    actions.appendChild(ready);
    card.appendChild(actions);
  }
  return card;
}

function applyPreset(s: FullState, me: PublicParticipant, deps: RoomPageDeps): void {
  const preset = getPreset();
  if (preset.length === 0) return;
  const have = new Set(me.picks.map((p) => p.gameId));
  const want = new Set(preset);
  for (const cur of have) if (!want.has(cur)) deps.socket.emit('togglePick', cur);
  for (const w of want) {
    if (!have.has(w) && s.games.some((g) => g.id === w)) deps.socket.emit('togglePick', w);
  }
}

function buildMyPickUI(me: PublicParticipant, s: FullState, deps: RoomPageDeps): HTMLElement {
  const wrap = el('div', 'my-picks');
  const heartedCount = me.picks.filter((p) => p.weighted).length;
  const summaryKey = `room.pickedSummary_${s.meta.type}` as 'room.pickedSummary_games';
  wrap.appendChild(el('div', 'pick-summary', t(summaryKey, { n: me.picks.length, h: heartedCount })));

  const grid = el('div', 'game-grid');
  const myPicks = new Map(me.picks.map((p) => [p.gameId, p]));
  const visible = s.games.filter((g) => !g.custom || g.createdBy === me.id || myPicks.has(g.id));
  const vetoedSet = new Set(s.vetoes.map((v) => v.gameId));

  const isDisabled = s.phase !== 'lobby';
  for (const g of visible) {
    const picked = myPicks.has(g.id);
    const pick = myPicks.get(g.id);
    const vetoed = vetoedSet.has(g.id);
    const tile = el('div', `tile ${picked ? 'picked' : ''} ${vetoed ? 'vetoed' : ''} ${isDisabled || vetoed ? 'is-disabled' : ''}`);
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', isDisabled || vetoed ? '-1' : '0');
    if (isDisabled || vetoed) tile.setAttribute('aria-disabled', 'true');
    tile.dataset.key = `tile-${g.id}`;
    tile.title = vetoed ? t('room.tile.vetoed', { name: g.name }) : g.name;
    if (g.logoUrl) {
      const img = document.createElement('img');
      img.src = g.logoUrl;
      img.alt = g.name;
      img.loading = 'lazy';
      tile.appendChild(img);
    } else {
      const colored = el('div', 'custom-tile-bg');
      colored.style.background = g.color ?? '#444';
      colored.textContent = g.name;
      tile.appendChild(colored);
    }
    tile.appendChild(el('div', 'tile-name', g.name));
    tile.addEventListener('click', (ev) => {
      if (tile.classList.contains('is-disabled')) return;
      if ((ev.target as HTMLElement).closest('.heart-btn, .remove-custom')) return;
      ev.preventDefault();
      deps.socket.emit('togglePick', g.id);
    });
    tile.addEventListener('keydown', (ev) => {
      if (tile.classList.contains('is-disabled')) return;
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        deps.socket.emit('togglePick', g.id);
      }
    });
    if (picked) {
      tile.appendChild(el('div', 'tile-badge', '✓'));
      const heartBtn = el('button', `heart-btn ${pick?.weighted ? 'on' : ''}`, '♥') as HTMLButtonElement;
      heartBtn.type = 'button';
      heartBtn.dataset.key = `heart-${g.id}`;
      heartBtn.title = pick?.weighted
        ? t('room.heart.remove')
        : heartedCount > 0
        ? t('room.heart.move')
        : t('room.heart.add');
      heartBtn.disabled = isDisabled;
      heartBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        deps.socket.emit('toggleHeart', g.id);
      });
      tile.appendChild(heartBtn);
    }
    if (g.custom && g.createdBy === me.id) {
      const x = el('button', 'remove-custom', '✕') as HTMLButtonElement;
      x.type = 'button';
      x.dataset.key = `remove-${g.id}`;
      x.title = t('room.removeCustom');
      x.disabled = isDisabled;
      x.addEventListener('click', (ev) => {
        ev.stopPropagation();
        deps.socket.emit('removeCustomGame', g.id);
      });
      tile.appendChild(x);
    }
    grid.appendChild(tile);
  }
  wrap.appendChild(grid);

  const add = el('div', 'custom-add');
  const ta = document.createElement('textarea');
  const phKey = `room.customPlaceholder.${s.meta.type}` as 'room.customPlaceholder.games';
  ta.placeholder = t(phKey);
  ta.maxLength = 800;
  ta.rows = 5;
  ta.disabled = s.phase !== 'lobby';
  const autoresize = () => {
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  };
  ta.addEventListener('input', autoresize);
  add.appendChild(ta);
  const addBtn = el('button', 'btn primary add-custom-btn', t('room.add')) as HTMLButtonElement;
  addBtn.disabled = s.phase !== 'lobby';
  addBtn.addEventListener('click', () => {
    const lines = ta.value.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    for (const name of lines) deps.socket.emit('addCustomGame', name);
    ta.value = '';
  });
  add.appendChild(addBtn);
  wrap.appendChild(add);
  return wrap;
}

function buildTheirPickList(p: PublicParticipant, s: FullState): HTMLElement {
  const wrap = el('div', 'their-picks');
  if (p.picks.length === 0) {
    wrap.appendChild(el('div', 'placeholder', t('room.noPicks')));
    return wrap;
  }
  const games = new Map(s.games.map((g) => [g.id, g]));
  const list = el('ul', 'their-pick-list');
  for (const pk of p.picks) {
    const g = games.get(pk.gameId);
    if (!g) continue;
    const li = el('li', `their-pick ${pk.weighted ? 'weighted' : ''}`);
    li.dataset.key = `their-pick-${p.id}-${pk.gameId}`;
    if (g.logoUrl) {
      const img = document.createElement('img');
      img.src = g.logoUrl;
      img.alt = '';
      li.appendChild(img);
    } else {
      const dot = el('span', 'their-color-dot');
      dot.style.background = g.color ?? '#888';
      li.appendChild(dot);
    }
    li.appendChild(el('span', 'their-name', g.name));
    if (pk.weighted) li.appendChild(el('span', 'heart-tag', '♥'));
    list.appendChild(li);
  }
  wrap.appendChild(list);
  return wrap;
}

function buildSidebar(s: FullState, me: PublicParticipant | undefined, deps: RoomPageDeps): HTMLElement {
  const side = el('aside', 'sidebar');
  side.appendChild(el('h2', 'side-h', t('room.master')));
  const games = new Map(s.games.map((g) => [g.id, g]));
  const inSlices = new Set(s.slices.map((sl) => sl.gameId));
  const downvoted2plus: Array<{ gameId: string; count: number }> = [];
  for (const [id, n] of Object.entries(s.downvoteCounts)) {
    if (n >= 2 && !inSlices.has(id) && !s.vetoes.some((v) => v.gameId === id)) {
      downvoted2plus.push({ gameId: id, count: 0 });
    }
  }
  const masterRows = [...s.slices, ...downvoted2plus];
  if (masterRows.length === 0) {
    side.appendChild(el('div', 'placeholder', t('room.masterEmpty')));
  } else {
    const list = el('ul', 'master-list');
    for (const sl of masterRows) {
      const g = games.get(sl.gameId);
      if (!g) continue;
      const dvCount = s.downvoteCounts[sl.gameId] ?? 0;
      const grayed = dvCount >= 2;
      const myDownvote = me?.downvotes.includes(sl.gameId) ?? false;
      const li = el('li', `master-item ${grayed ? 'grayed' : ''} ${myDownvote ? 'i-downvoted' : ''}`);
      li.dataset.key = `master-${sl.gameId}`;
      if (g.logoUrl) {
        const img = document.createElement('img');
        img.src = g.logoUrl;
        img.alt = '';
        li.appendChild(img);
      } else {
        const dot = el('span', 'master-color-dot');
        dot.style.background = g.color ?? '#888';
        li.appendChild(dot);
      }
      li.appendChild(el('span', 'master-name', g.name));
      if (!grayed && sl.count !== 1) {
        const labelStr = sl.count >= 2 ? `×${sl.count}` : `×${formatHalves(sl.count)}`;
        li.appendChild(el('span', `master-mult ${sl.count > 1 ? 'boost' : 'cut'}`, labelStr));
      }
      if (grayed) li.appendChild(el('span', 'master-mult cut', t('room.outLabel')));
      const dv = el('button', `downvote-btn ${myDownvote ? 'on' : ''}`, '⬇') as HTMLButtonElement;
      dv.type = 'button';
      dv.dataset.key = `downvote-${sl.gameId}`;
      dv.title = myDownvote
        ? t('room.dv.remove')
        : grayed
        ? t('room.dv.grayedOut')
        : t('room.dv.title', { n: dvCount });
      dv.disabled = s.phase !== 'lobby';
      dv.addEventListener('click', () => deps.socket.emit('toggleDownvote', sl.gameId));
      li.appendChild(dv);
      list.appendChild(li);
    }
    side.appendChild(list);
  }
  if (s.vetoes.length > 0) {
    side.appendChild(el('h3', 'side-h-sub', t('room.vetoed')));
    const vlist = el('ul', 'vetoed-list');
    for (const v of s.vetoes) {
      const g = games.get(v.gameId);
      if (!g) continue;
      const li = el('li', 'vetoed-item');
      li.dataset.key = `vetoed-${v.gameId}`;
      li.appendChild(el('span', 'vetoed-name', g.name));
      const vetoer = s.participants.find((p) => p.id === v.by);
      if (vetoer) li.appendChild(el('span', 'vetoed-by', t('room.vetoedBy', { name: vetoer.name })));
      if (me && v.by === me.id) {
        const undo = el('button', 'btn small ghost', t('room.undo')) as HTMLButtonElement;
        undo.type = 'button';
        undo.dataset.key = `unveto-${v.gameId}`;
        undo.disabled = s.phase !== 'lobby';
        undo.title = t('room.undoTitle');
        undo.addEventListener('click', () => deps.socket.emit('unveto', v.gameId));
        li.appendChild(undo);
      }
      vlist.appendChild(li);
    }
    side.appendChild(vlist);
  }
  const ready = s.participants.filter((p) => p.ready && p.connected).length;
  const total = s.participants.filter((p) => p.connected).length;
  side.appendChild(el('div', 'side-footer',
    t('room.footerStatus', { ready, total, pool: s.slices.length })));
  return side;
}

function formatHalves(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function buildCountdownOverlay(s: FullState): HTMLElement {
  const overlay = el('div', 'overlay countdown-overlay');
  const num = el('div', 'countdown-num');
  overlay.appendChild(num);
  overlay.appendChild(el('div', 'countdown-sub', t('room.spinning')));
  const tick = () => {
    const remain = Math.max(0, (s.countdownUntil ?? 0) - Date.now());
    num.textContent = String(Math.ceil(remain / 1000));
    if (remain > 0 && document.body.contains(overlay)) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  return overlay;
}

function buildWheelOverlay(
  s: FullState,
  me: PublicParticipant | undefined,
  deps: RoomPageDeps,
): HTMLElement {
  const overlay = el('div', 'overlay wheel-overlay');
  const stage = el('div', 'wheel-stage');
  const canvas = document.createElement('canvas');
  canvas.className = 'wheel-canvas';
  stage.appendChild(canvas);
  overlay.appendChild(stage);

  if (s.phase === 'result' && s.spin) {
    const game = s.games.find((g) => g.id === s.spin!.gameId);
    const panel = el('div', 'result-panel');
    const ebKey = `room.eyebrow.${s.meta.type}` as 'room.eyebrow.games';
    panel.appendChild(el('div', 'result-eyebrow', t(ebKey)));
    panel.appendChild(el('div', 'result-name', game?.name ?? '???'));
    if (game?.logoUrl) {
      const img = document.createElement('img');
      img.className = 'result-logo';
      img.src = game.logoUrl;
      panel.appendChild(img);
    }
    const buttons = el('div', 'result-actions');
    const accept = el('button', 'btn primary', t('room.startNew')) as HTMLButtonElement;
    accept.addEventListener('click', () => deps.socket.emit('acceptResult'));
    buttons.appendChild(accept);
    const veto = el('button', 'btn danger', me?.vetoUsed ? t('room.vetoUsed') : t('room.vetoButton')) as HTMLButtonElement;
    veto.disabled = !!me?.vetoUsed;
    veto.title = t('room.vetoTitle');
    veto.addEventListener('click', () => {
      if (confirm(t('room.vetoConfirm', { name: game?.name ?? '???' }))) {
        deps.socket.emit('veto');
      }
    });
    buttons.appendChild(veto);
    if (!s.revote) {
      const rev = el('button', 'btn warn', t('room.requestRespin')) as HTMLButtonElement;
      rev.title = t('room.respinTitle');
      rev.addEventListener('click', () => deps.socket.emit('proposeRevote'));
      buttons.appendChild(rev);
    } else {
      const totalConn = s.participants.filter((p) => p.connected).length;
      const haveVoted = s.revote.votes.length;
      const meVoted = me ? s.revote.votes.includes(me.id) : false;
      buttons.appendChild(el('div', 'revote-status',
        t('room.respinStatus', { voted: haveVoted, total: totalConn })));
      if (!meVoted) {
        const yes = el('button', 'btn primary', t('room.respinYes')) as HTMLButtonElement;
        yes.addEventListener('click', () => deps.socket.emit('voteRevote', true));
        buttons.appendChild(yes);
        const no = el('button', 'btn ghost', t('room.respinNo')) as HTMLButtonElement;
        no.addEventListener('click', () => deps.socket.emit('voteRevote', false));
        buttons.appendChild(no);
      } else {
        buttons.appendChild(el('div', 'revote-waiting', t('room.respinWaiting')));
      }
    }
    panel.appendChild(buttons);
    overlay.appendChild(panel);
  }
  return overlay;
}
